// ✅ Step 1: Export initialState from each slice file (you've done this)
// ✅ Step 2: Setup preloadedState with proper merging based on version

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import messagesReducer, { initialState as messagesInitial } from "./slices/messagesSlice";
import orderReducer, { initialState as ordersInitial } from "./slices/ordersSlice";
import inventoryReducer, { initialState as inventoryInitial } from "./slices/inventorySlice";
import contractsReducer, { initialState as contractsInitial } from "./slices/contractsSlice";
import shipmentReducer, { initialState as shipmentsInitial } from "./slices/shipmentSlice";
import invoiceReducer, { initialState as invoicesInitial } from "./slices/invoiceSlice";
import documentUploaderReducer,  { initialState as documentUploaderInitial } from "./slices/documentUploaderSlice";
import appointmentsReducer, { initialState as appointmentsInitial } from "./slices/appointmentsSlices";
import manageTeamReducer, { initialState as manageTeamInitial } from "./slices/manageTeamSlice";
import notificationCenterReducer, { initialState as notificationCenterInitial } from "./slices/NotificationCenterSlice"
import locationReducer, { initialState as locationInitial } from "./slices/locationSlice";
import resourceSelectionReducer, {
  initialState as resourceSelectionInitial,
} from "./slices/resourceSelectionSlice";
import dynamicTabLimitMiddleware from "./middleware/dynamicTabLimitMiddleware";



const SLICE_INITIAL_STATES = {
  messages: messagesInitial,
  orders: ordersInitial,
  inventory: inventoryInitial,
  contracts: contractsInitial,
  shipments: shipmentsInitial,
  invoices: invoicesInitial,
  documentUploader: documentUploaderInitial,
  appointments: appointmentsInitial,
  manageTeam: manageTeamInitial,
  notificationCenter: notificationCenterInitial,
  locations: locationInitial,
  resourceSelection: resourceSelectionInitial,
};

export const RESET_STORE = "redux/RESET_STORE";

const appReducer = combineReducers({
  messages: messagesReducer,
  orders: orderReducer,
  inventory: inventoryReducer,
  shipments: shipmentReducer,
  invoices: invoiceReducer,
  contracts: contractsReducer,
  documentUploader: documentUploaderReducer,
  appointments: appointmentsReducer,
  manageTeam: manageTeamReducer,
  notificationCenter: notificationCenterReducer,
  locations: locationReducer,
  resourceSelection: resourceSelectionReducer,
});

const rootReducer = (state, action) => {
  if (action.type === RESET_STORE) {
    return appReducer(undefined, action);
  }

  return appReducer(state, action);
};

const saveState = (state) => {
  try {
    localStorage.setItem("reduxState", JSON.stringify(state));
  } catch (error) {
    console.error("State save error:", error);
  }
};

const APPOINTMENTS_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
  selectedTemplate: persistedSlice.selectedTemplate ?? null,
  warehousesByLocationId: persistedSlice.warehousesByLocationId || {},
  fetchedWarehouseIds: persistedSlice.fetchedWarehouseIds || [],
  lastSelectedLocation: persistedSlice.lastSelectedLocation ?? null,
  lastSelectedWarehouse: persistedSlice.lastSelectedWarehouse ?? null,
  lastSelectedDate: persistedSlice.lastSelectedDate ?? null,
  lastSettingsLocation: persistedSlice.lastSettingsLocation ?? null,
  lastSettingsWarehouse: persistedSlice.lastSettingsWarehouse ?? null,
});

const SHIPMENTS_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
  selectedTemplate: persistedSlice.selectedTemplate ?? null,
  statusFilter: Object.prototype.hasOwnProperty.call(
    persistedSlice || {},
    "statusFilter"
  )
    ? persistedSlice.statusFilter
    : currentInitial.statusFilter,
  listViewState: persistedSlice.listViewState || currentInitial.listViewState,
});

const INVOICES_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
  selectedTemplate: persistedSlice.selectedTemplate ?? null,
  listViewState: persistedSlice.listViewState || currentInitial.listViewState,
});

const ORDERS_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
  selectedTemplate: persistedSlice.selectedTemplate ?? null,
  statusFilter: Object.prototype.hasOwnProperty.call(
    persistedSlice || {},
    "statusFilter"
  )
    ? persistedSlice.statusFilter
    : currentInitial.statusFilter,
  listViewState: persistedSlice.listViewState || currentInitial.listViewState,
});

const LOCATIONS_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
  listViewState: persistedSlice.listViewState || currentInitial.listViewState,
});

const RESOURCE_SELECTION_MERGER = (currentInitial, persistedSlice) => ({
  ...currentInitial,
  dynamicTabs: persistedSlice.dynamicTabs || [],
  activeTab: persistedSlice.activeTab ?? 0,
});

const loadState = () => {
  try {
    const serialized = localStorage.getItem("reduxState");
    if (!serialized) return undefined;

    const persistedState = JSON.parse(serialized);

    const mergedState = Object.keys(SLICE_INITIAL_STATES).reduce((acc, sliceKey) => {
      const persistedSlice = persistedState[sliceKey];
      const currentInitial = SLICE_INITIAL_STATES[sliceKey];

      if (persistedSlice?._version === currentInitial._version) {
        if (sliceKey === "appointments") {
          return {
            ...acc,
            [sliceKey]: APPOINTMENTS_MERGER(currentInitial, persistedSlice),
          };
        }

        if (sliceKey === "shipments") {
          return {
            ...acc,
            [sliceKey]: SHIPMENTS_MERGER(currentInitial, persistedSlice),
          };
        }

        if (sliceKey === "invoices") {
          return {
            ...acc,
            [sliceKey]: INVOICES_MERGER(currentInitial, persistedSlice),
          };
        }

        if (sliceKey === "orders") {
          return {
            ...acc,
            [sliceKey]: ORDERS_MERGER(currentInitial, persistedSlice),
          };
        }

        if (sliceKey === "locations") {
          return {
            ...acc,
            [sliceKey]: LOCATIONS_MERGER(currentInitial, persistedSlice),
          };
        }

        if (sliceKey === "resourceSelection") {
          return {
            ...acc,
            [sliceKey]: RESOURCE_SELECTION_MERGER(currentInitial, persistedSlice),
          };
        }

        return {
          ...acc,
          [sliceKey]: {
            ...currentInitial,
            dynamicTabs: persistedSlice.dynamicTabs || [],
            activeTab: persistedSlice.activeTab ?? 0,
            selectedTemplate: persistedSlice.selectedTemplate ?? null,
          },
        };
      }

      return {
        ...acc,
        [sliceKey]: currentInitial,
      };
    }, {});

    return mergedState;
  } catch (error) {
    console.error("State load error:", error);
    return undefined;
  }
};

const store = configureStore({
  reducer: rootReducer,
  preloadedState: loadState(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["messages/resetTabs"],
        ignoredPaths: [
          "*.dynamicTabs.*.createdAt",
          "*.dynamicTabs.*.data",
          "messages.dynamicTabs",
          "invoices.dynamicTabs.*.data",
          "shipments.dynamicTabs.*.data",
          "documentUploader.dynamicTabs.*.data",
          "appointments.dynamicTabs.*.data",
          "manageTeam.dynamicTabs.*.data",
          "notificationCenter.dynamicTabs.*.data",
          "locations.dynamicTabs.*.data",
          "resourceSelection.dynamicTabs.*.data",
        ],
      },
    }).concat(dynamicTabLimitMiddleware),
});

const createDebouncedSave = () => {
  let pendingSave = null;
  return {
    trigger: (state) => {
      if (pendingSave) return;
      pendingSave = setTimeout(() => {
        saveState(state);
        pendingSave = null;
      }, 1000);
    },
    flush: (state) => {
      if (pendingSave) {
        clearTimeout(pendingSave);
        pendingSave = null;
      }
      saveState(state);
    },
  };
};

const { trigger, flush } = createDebouncedSave();

let previousState = {
  messages: { dynamicCount: 0 },
  inventory: { dynamicCount: 0 },
  orders: { dynamicCount: 0 },
  shipments: { dynamicCount: 0 },
  invoices: { dynamicCount: 0 },
  contracts: { dynamicCount: 0 },
  payments: { dynamicCount: 0 },
  documentUploader:{ dynamicCount: 0 },
  appointments: { dynamicCount: 0 },
  manageTeam: { dynamicCount: 0 },
  notificationCenter: { dynamicCount: 0 },
  locations: { dynamicCount: 0 },
  resourceSelection: { dynamicCount: 0 },
};

store.subscribe(() => {
  const currentState = store.getState();

  Object.keys(currentState).forEach((sliceKey) => {
    const slice = currentState[sliceKey];
    const prevCount = previousState[sliceKey]?.dynamicCount || 0;

    if (slice.dynamicTabs.length !== prevCount) {
      flush(currentState);
      previousState[sliceKey] = {
        dynamicCount: slice.dynamicTabs.length,
      };
    }
  });

  trigger(currentState);
});

export function resetReduxState() {
  localStorage.removeItem("reduxState");
  store.dispatch({ type: RESET_STORE });
}

export default store;